import {
  GidType,
  parseGid,
  toGid,
} from "@redotech/redo-model/shopify/gid-utils";
import {
  ExtensionShopifyClient,
  ShopifyAjaxClient,
  ShopifyCart,
  ShopifyCartAttributes,
  ShopifyCartChange,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyProductSellingPlan,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";

interface CartCreateResponse {
  id: string;
  checkoutUrl: string;
  attributes: Array<{ key: string; value: string }>;
  lines: { edges: Array<{ node: CartLine }> };
  cost: { totalAmount: MoneyV2; subtotalAmount: MoneyV2 };
}

interface CartLine {
  id: string;
  quantity: number;
  attributes: Array<unknown>; // Or define a more specific interface if attributes have a known structure
  merchandise: Merchandise;
}

interface Merchandise {
  id: string;
  title: string;
  product: Product;
}

interface Product {
  id: string;
  title: string;
}

interface MoneyV2 {
  amount: string;
  currencyCode: string;
}

interface CartCreateInput {
  lines: {
    merchandiseId: string;
    quantity: number;
    attributes?: { key: string; value: string }[];
  }[];
  attributes?: { key: string; value: string }[];
  discountCodes?: string[];
}

export interface BuyerIdentity {
  purchasingCompany?: { company?: { name?: string } };
}

export class StorefrontClient implements ExtensionShopifyClient {
  type = ShopifyClientType.SHOPIFYGRAPHQL;
  constructor(
    private readonly host: string,
    private readonly auth: string,
    private readonly cartId: string,
  ) {}

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   */
  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params: URLSearchParams,
    preventReload?: boolean,
  ): Promise<void> {
    if (data.items.length !== 1) {
      return;
    }
    const query = `
      mutation CartLineAdd($cartId: ID!, $line: CartLineInput!) {
        cartLinesAdd(cartId: $cartId, lines: [$line]) {
          cart {
            totalQuantity
          }
        }
      }
    `;

    const properties: { [key: string]: string } =
      data.items?.[0]?.properties ?? {};
    const attributes: ShopifyCartAttributes = { attributes: [] };
    for (const key in properties) {
      const value = properties[key];
      if (value) {
        attributes.attributes.push({ key, value });
      }
    }

    const response = await fetch(
      `https://${this.host}/api/2024-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: {
            cartId: `gid://shopify/Cart/${this.cartId}`,
            line: {
              quantity: data.items[0]?.quantity,
              merchandiseId: `gid://shopify/ProductVariant/${data.items[0]?.id ?? ""}`,
              attributes: attributes.attributes,
              sellingPlanId: data.items[0]?.selling_plan
                ? toGid(GidType.SELLING_PLAN, data.items[0]?.selling_plan)
                : undefined,
            },
          },
        }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to add item to cart");
    }
    if (!preventReload) {
      location.reload();
    }
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   */
  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    const query = `
    query GetCart($id: ID!) {
			cart(id: $id) {
				id
        attributes {
          key
          value
        }
        lines(first: 250) {
          edges {
            node {
              attributes {
                key
                value
              }
              id
              cost {
                totalAmount {
                  amount
                }
              }
              quantity
              sellingPlanAllocation {
                sellingPlan {
                  id
                  name
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  product {
                      handle
                      id
                      title
                      vendor
                  }
                  requiresShipping
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
          }
        }
			}
		}
    `;
    const response = await fetch(
      `https://${this.host}/api/2024-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: { id: `gid://shopify/Cart/${this.cartId}` },
        }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to fetch cart");
    }
    const json = await response.json();
    const cartData = json.data?.cart;

    return {
      attributes: cartData
        ? Object.fromEntries(
            cartData.attributes.map((attr: { key: string; value: string }) => [
              attr.key,
              attr.value,
            ]),
          )
        : {},
      items: cartData
        ? cartData.lines.edges.map(
            (edge: any): ShopifyCartItem => ({
              properties:
                edge.node.attributes?.reduce(
                  (
                    acc: { [key: string]: string },
                    attr: { key: string; value: string },
                  ) => {
                    acc[attr.key] = attr.value;
                    return acc;
                  },
                  {} as { [key: string]: string },
                ) || null,
              id: edge.node.id,
              quantity: edge.node.quantity,
              product_id: +parseGid(edge.node.merchandise.product.id),
              variant_id: +parseGid(edge.node.merchandise.id),
              handle: edge.node.merchandise.product.handle,
              title: edge.node.merchandise.product.title,
              vendor: edge.node.merchandise.product.vendor,
              requires_shipping: edge.node.merchandise.requiresShipping,
              line_price: +edge.node.cost.totalAmount.amount * 100,
              selling_plan: edge.node.sellingPlanAllocation?.sellingPlan?.id
                ? +parseGid(edge.node.sellingPlanAllocation?.sellingPlan?.id)
                : undefined,
            }),
          )
        : [],
      currency: cartData?.cost?.totalAmount?.currencyCode,
      total_price: cartData?.cost?.totalAmount?.amount,
      subtotal_price: cartData?.cost?.subtotalAmount?.amount,
    };
  }

  async getBuyerIdentity(signal?: AbortSignal): Promise<BuyerIdentity> {
    const query = `
      query GetCart($id: ID!) {
  			cart(id: $id) {
     			buyerIdentity {
                purchasingCompany {
                  company {
                    name
                  }
                }
              }
  			}
		}
      `;
    const response = await fetch(
      `https://${this.host}/api/2024-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: { id: `gid://shopify/Cart/${this.cartId}` },
        }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to fetch buyer identity");
    }
    const json = await response.json();
    const cartData = json.data?.cart;
    return { purchasingCompany: cartData?.buyerIdentity?.purchasingCompany };
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async cartChange(
    change: ShopifyCartChange,
    signal?: AbortSignal,
  ): Promise<void> {
    if (change.quantity) {
      return;
    }
    const query = `
      mutation CartLineRemove($cartId: ID!, $line: ID!) {
        cartLinesRemove(cartId: $cartId, lineIds: [$line]) {
          cart {
            totalQuantity
          }
        }
      }
    `;
    const response = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: {
            cartId: `gid://shopify/Cart/${this.cartId}`,
            line: change.id,
          },
        }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to remove item from cart");
    }
    location.reload();
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async updateCartAttribute(attributes: ShopifyCartAttributes): Promise<void> {
    const cartId = `gid://shopify/Cart/${this.cartId}`;
    const getQuery = `
      query GetCartAttributes($cartId: ID!) {
        cart(id: $cartId) {
          attributes {
            key
            value
          }
        }
      }
    `;
    const getResponse = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({ query: getQuery, variables: { cartId } }),
      },
    );
    if (!getResponse.ok) {
      throw new Error("Failed to get cart attributes");
    }
    const getJson = await getResponse.json();
    const combinedAttributes = [
      ...getJson.data.cart.attributes,
      ...attributes.attributes,
    ];
    const query = `
      mutation CartSetAttribute($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
          cart {
            id
            attributes {
              key
              value
            }
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: { attributes: combinedAttributes, cartId },
        }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to set cart attribute");
    }
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
  ): Promise<undefined> {
    const newQuantity = item.quantity - numToRemove;
    await this.cartChange(
      { id: String(item.id), quantity: newQuantity },
      signal,
    );
  }

  async updateCartItemSellingPlan(): Promise<undefined> {}

  async cartClear(): Promise<void> {}

  /**
   * GET /widgets/:widget_id/collections
   */
  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    if (!handle) {
      console.warn("Shopify product handle is missing.");
      return undefined;
    }

    const getAllCollectionsForProductsQuery = `
        query GetAllCollectionsForProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              collections(first: 150) {
                nodes {
                  title
                  handle
                }
              }
            }
          }
        }
      `;

    const productId = `gid://shopify/Product/${handle}`;

    try {
      const response = await fetch(
        `https://${this.host}/api/2025-01/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": this.auth,
          },
          body: JSON.stringify({
            query: getAllCollectionsForProductsQuery,
            variables: { ids: [productId] },
          }),
        },
      );

      if (!response.ok) {
        console.error(
          `HTTP error from Shopify Storefront! status: ${response.status}`,
        );
        return undefined;
      }

      const data = await response.json();

      if (data.errors) {
        console.error("GraphQL errors from Shopify Storefront:", data.errors);
        return undefined;
      }

      const collections =
        data.data?.nodes
          ?.filter((node: any) => node != null)
          .flatMap((node: any) => {
            return node.collections?.nodes?.map((collection: any) => {
              return { title: collection.title, handle: collection.handle };
            });
          }) || [];

      return collections;
    } catch (error) {
      console.error(
        "Error fetching collections from Shopify Storefront:",
        error,
      );
      return undefined;
    }
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/product#get-locale-products-product-handle-js
   */
  async productGet(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProduct> {
    return ShopifyAjaxClient.fromEnv().productGet(handle, signal);
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductJS> {
    return ShopifyAjaxClient.fromEnv().productGetJS(handle, signal);
  }

  async createCart(
    input: CartCreateInput,
    signal?: AbortSignal,
  ): Promise<CartCreateResponse> {
    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
            attributes {
              key
              value
            }
            lines(first: 10) {
              edges {
                node {
                  id
                  quantity
                  attributes {
                    key
                    value
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      product {
                        id
                        title
                      }
                    }
                  }
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = { input };

    const response = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({ query, variables }),
        signal,
      },
    );

    const responseData = await response.json();

    if (!response.ok || responseData.errors) {
      console.error("Shopify API Error:", responseData.errors);
      const errorMessage = responseData.errors
        ? responseData.errors[0].message
        : "Failed to create cart";
      throw new Error(errorMessage);
    }

    const createdCart = responseData.data.cartCreate.cart as CartCreateResponse;

    if (!createdCart) {
      throw new Error(
        "Cart creation was successful, but no cart data was returned.",
      );
    }

    return createdCart;
  }

  async productRecommendations(): Promise<never> {
    throw new Error("Unsupported");
  }

  async productsGet(): Promise<never> {
    throw new Error("Unsupported");
  }

  async getStorefrontAccessTokens(accessToken: string): Promise<never> {
    throw new Error("Unsupported");
  }

  async deleteStorefrontAccesstoken(): Promise<never> {
    throw new Error("Unsupported");
  }

  async createStorefrontAccessToken(): Promise<never> {
    throw new Error("Unsupported");
  }

  async productsSearch(): Promise<never> {
    throw new Error("Unsupported");
  }

  async getShopMetafield(data: {
    key: string;
    namespace: string;
  }): Promise<ShopifyShop | undefined> {
    const query = `
      query GetShopMetafield($key: String!, $namespace: String) {
        shop {
          id
          metafield(key: $key, namespace: $namespace) {
            id
            key
            namespace
            value
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: { key: data.key, namespace: data.namespace },
        }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to fetch shop metafield");
    }
    const json = await response.json();
    return json.data.shop as ShopifyShop;
  }

  async deleteCartAttribute(data: { key: string }): Promise<void> {
    const cartId = `gid://shopify/Cart/${this.cartId}`;
    const getQuery = `
      query GetCartAttributes($cartId: ID!) {
        cart(id: $cartId) {
          attributes {
            key
            value
          }
        }
      }
    `;
    const getResponse = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({ query: getQuery, variables: { cartId } }),
      },
    );
    if (!getResponse.ok) {
      throw new Error("Failed to get cart attributes");
    }
    const getJson = await getResponse.json();
    const attributes = getJson.data.cart.attributes.filter(
      (attribute: { key: string }) => attribute.key !== data.key,
    );

    const query = `
      mutation CartSetAttribute($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
          cart {
            id
            attributes {
              key
              value
            }
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({ query, variables: { attributes, cartId } }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to set cart attribute");
    }
  }

  async getProductSellingPlans(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductSellingPlan[] | undefined> {
    const query = /* GraphQL */ `
      query productWithSellingPlans($handle: String!) {
        product(handle: $handle) {
          id
          sellingPlanGroups(first: 10) {
            edges {
              node {
                name
                sellingPlans(first: 10) {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({ query, variables: { handle } }),
        signal,
      },
    );

    if (!response.ok) {
      console.error("Failed to fetch product selling plans", response);
      return undefined;
    }

    const { data } = await response.json();

    if (!data?.product?.sellingPlanGroups) {
      return undefined;
    }

    // Flatten all selling plans from all groups
    const sellingPlans: ShopifyProductSellingPlan[] =
      data.product.sellingPlanGroups.edges.flatMap((group: any) =>
        group.node.sellingPlans.edges.map((plan: any) => ({
          id: plan.node.id,
          name: plan.node.name,
        })),
      );

    return sellingPlans;
  }

  async getProductSellingPlansByGid(
    productGid: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductSellingPlan[] | undefined> {
    const query = /* GraphQL */ `
      query productWithSellingPlansByGid($productGid: ID!) {
        product(id: $productGid) {
          id
          sellingPlanGroups(first: 10) {
            edges {
              node {
                name
                sellingPlans(first: 10) {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.host}/api/2023-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({ query, variables: { productGid } }),
        signal,
      },
    );

    if (!response.ok) {
      console.error("Failed to fetch product selling plans by GID", response);
      return undefined;
    }

    const { data } = await response.json();

    if (!data?.product?.sellingPlanGroups) {
      return undefined;
    }

    // Flatten all selling plans from all groups
    const sellingPlans: ShopifyProductSellingPlan[] =
      data.product.sellingPlanGroups.edges.flatMap((group: any) =>
        group.node.sellingPlans.edges.map((plan: any) => ({
          id: plan.node.id,
          name: plan.node.name,
        })),
      );

    return sellingPlans;
  }

  async applyDiscountCode(
    discountCode: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const query = /* GraphQL */ `
      mutation cartDiscountCodesUpdate(
        $cartId: ID!
        $discountCodes: [String!]
      ) {
        cartDiscountCodesUpdate(
          cartId: $cartId
          discountCodes: $discountCodes
        ) {
          cart {
            id
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.host}/api/2025-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: {
            cartId: `gid://shopify/Cart/${this.cartId}`,
            discountCodes: [discountCode],
          },
        }),
        signal,
      },
    );

    if (!response.ok) {
      throw new Error("Failed to apply discount code");
    }

    const json = await response.json();
    if (json.data?.cartDiscountCodesUpdate?.userErrors?.length > 0) {
      throw new Error(json.data.cartDiscountCodesUpdate.userErrors[0].message);
    }
  }

  async removeDiscountCodes(signal?: AbortSignal): Promise<void> {
    const query = /* GraphQL */ `
      mutation cartDiscountCodesUpdate(
        $cartId: ID!
        $discountCodes: [String!]
      ) {
        cartDiscountCodesUpdate(
          cartId: $cartId
          discountCodes: $discountCodes
        ) {
          cart {
            id
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.host}/api/2025-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: {
            cartId: `gid://shopify/Cart/${this.cartId}`,
            discountCodes: [],
          },
        }),
        signal,
      },
    );

    if (!response.ok) {
      throw new Error("Failed to remove discount codes");
    }

    const json = await response.json();
    if (json.data?.cartDiscountCodesUpdate?.userErrors?.length > 0) {
      throw new Error(json.data.cartDiscountCodesUpdate.userErrors[0].message);
    }
  }

  async updateLineItemAttributes(
    updates: Array<{
      lineItemId: string;
      attributes: Array<{ key: string; value: string }>;
    }>,
    signal?: AbortSignal,
  ): Promise<void> {
    const query = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            id
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const lines = updates.map((update) => ({
      id: update.lineItemId,
      attributes: update.attributes,
    }));

    const response = await fetch(
      `https://${this.host}/api/2024-10/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.auth,
        },
        body: JSON.stringify({
          query,
          variables: { cartId: `gid://shopify/Cart/${this.cartId}`, lines },
        }),
        signal,
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update line item attributes");
    }

    const json = await response.json();
    if (json.data?.cartLinesUpdate?.userErrors?.length > 0) {
      throw new Error(json.data.cartLinesUpdate.userErrors[0].message);
    }
  }
}
